import cn from 'classnames';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import applyStyleToAllConditionTextsOperation from 'editor/src/store/design/operation/applyStyleToAllConditionTextsOperation';
import deleteConditionOperation from 'editor/src/store/design/operation/deleteConditionOperation';
import updateConditionActiveOptionOperation from 'editor/src/store/design/operation/updateConditionActiveOptionOperation';
import getConditionChildrenKey from 'editor/src/store/design/reducer/getConditionChildrenKey';
import { ConditionGroup, Condition } from 'editor/src/store/design/types';
import removeAllSelectedMediaElementsOperation from 'editor/src/store/editor/operation/removeAllSelectedMediaElementsOperation';
import { useDispatch } from 'editor/src/store/hooks';

import { logEvent } from 'editor/src/amplitude';

import ConditionElements from 'editor/src/component/ConditionGroupBuilder/ConditionElements';
import useDragHandle from 'editor/src/component/ConditionGroupBuilder/ConditionElements/ReorganizableList/useDragHandle';
import useOverlay from 'editor/src/component/ConditionGroupBuilder/Overlay/useOverlay';
import IconApplyToAllAreas from 'editor/src/component/Icon/IconApplyToAllAreas';
import IconBin from 'editor/src/component/Icon/IconBin';
import IconEdit from 'editor/src/component/Icon/IconEdit';
import PersonalizationElements from 'editor/src/component/PersonalizationContent/PersonalizationElements/index';
import SettingsMenu from 'editor/src/component/PersonalizationContent/PersonalizationElements/SettingsMenu';
import SettingsMenuItem from 'editor/src/component/PersonalizationContent/PersonalizationElements/SettingsMenu/SettingsMenuItem';
import useFocusElementOnFlag from 'editor/src/component/PersonalizationContent/PersonalizationElements/useFocusElementOnFlag';

import ConditionOptions from './ConditionOptions';
import ConditionSettings from './ConditionSettings';

import styles from './index.module.scss';

interface Props {
  spreadIndex: number;
  conditionGroup: ConditionGroup;
  condition: Condition;
  isBuilding: boolean;
  textOnly: boolean;
  /** This card is BORROWED: the condition is design-scoped
   *  (`showOnAllSpreads`) but stored on another spread, and this spread's panel
   *  renders it so neither buyer nor author has to go looking for it.
   *
   *  Drops the ELEMENTS linked to the active option — they live on the owning
   *  spread and belong in its panel, not repeated on every page. Nested
   *  picklists are kept in the buyer view: they are controls, not content, and
   *  the reason the parent is shown everywhere applies to them just as much.
   *
   *  Combined with `isBuilding` it also marks the card as foreign: still fully
   *  editable through the menu below, but it cannot be reordered, does not
   *  offer the actions that reach into elements it is not showing, and does not
   *  duplicate the owning panel's authoring tree. */
  optionsOnly?: boolean;
  onConditionCreated?: (conditionId: string, edit: () => void) => void;
}

function PersonalizationCondition({
  condition,
  spreadIndex,
  conditionGroup,
  isBuilding,
  onConditionCreated,
  textOnly,
  optionsOnly,
}: Props) {
  const children = conditionGroup.children[getConditionChildrenKey(condition.id, condition.activeOptionId)] || [];
  const dispatch = useDispatch();
  const dragHandle = useDragHandle();
  const { t } = useTranslation();

  const conditionEditorOverlay = useOverlay();
  const divRef = useRef<HTMLDivElement>(null);

  function onSelectOption(conditionId: string, optionId: string) {
    dispatch(updateConditionActiveOptionOperation({ spreadIndex, conditionId }, optionId));
  }

  function onEdit() {
    dispatch(removeAllSelectedMediaElementsOperation());
    const rect = divRef.current?.getBoundingClientRect();
    conditionEditorOverlay.open(rect);
  }

  function onDelete() {
    dispatch(deleteConditionOperation({ spreadIndex, conditionId: condition.id }));
    logEvent('condtion deleted', undefined);
  }

  function onApplyStyleToAll() {
    dispatch(
      applyStyleToAllConditionTextsOperation(spreadIndex, { spreadIndex, conditionId: condition.id }, condition),
    );
  }

  // Only the owning spread's card sits in the reorderable list, so only it can
  // be dragged. A foreign card still gets the menu — an author who has
  // navigated to page 2 must be able to rename an option or add a text variant
  // without first working out which page stores the picklist.
  const draggable = isBuilding && !optionsOnly;

  // See `optionsOnly`: a borrowed card keeps the nested picklists and drops the
  // elements. Without this the buyer could pick an option here and never be
  // offered the follow-up choice it reveals.
  const visibleChildren = optionsOnly ? children.filter((child) => child.type === 'condition') : children;

  useFocusElementOnFlag(conditionEditorOverlay.visible, divRef);

  useEffect(() => {
    onConditionCreated?.(condition.id, onEdit);
  }, []);

  return (
    <div
      ref={divRef}
      className={cn(styles.PersonalizationCondition, 'cy-personalization-condition', {
        [styles.isEditing]: conditionEditorOverlay.visible,
        [styles.static]: !isBuilding,
      })}
    >
      <div
        className={cn({ [styles.content]: draggable, [styles.menuHost]: isBuilding && !draggable }, styles.condition)}
        onMouseDown={draggable ? dragHandle.onMouseDown : undefined}
      >
        <div className={cn(styles.name, 'cy-personalization-condition-name')}>
          {condition.name}
          {isBuilding && (
            <>
              <SettingsMenu className={cn(styles.menuBtn, 'cy-more-button-personalization')}>
                <SettingsMenuItem className={cn(styles.button, 'cy-edit')} onClick={onEdit}>
                  <IconEdit className={styles.editIcon} />
                  {t('editor-edit')}
                </SettingsMenuItem>

                {/* Both reach into the elements linked to this condition, which
                    a foreign card is not showing — offer them where the author
                    can see what they affect. */}
                {!optionsOnly && (
                  <SettingsMenuItem className={cn(styles.button, 'cy-remove')} onClick={onDelete}>
                    <IconBin />
                    {t('editor-remove')}
                  </SettingsMenuItem>
                )}

                {!optionsOnly && condition.options.length > 1 && (
                  <SettingsMenuItem className={cn(styles.button, 'cy-apply-style-to-all')} onClick={onApplyStyleToAll}>
                    <IconApplyToAllAreas />
                    {t('editor-apply-text-style-to-all', 'Apply text style to all options')}
                  </SettingsMenuItem>
                )}
              </SettingsMenu>
              {conditionEditorOverlay.render(
                t('editor-condition-settings'),
                <ConditionSettings condition={condition} conditionGroup={conditionGroup} spreadIndex={spreadIndex} />,
              )}
            </>
          )}
        </div>
        {condition.description && (
          <div className={cn(styles.description, 'cy-personalization-condition-description')}>
            {condition.description}
          </div>
        )}
        <ConditionOptions condition={condition} onSelectOption={onSelectOption} />
      </div>
      {/* The authoring tree belongs to the owning spread's panel only: a
          borrowed card is a remote control, captioned with where the picklist
          is stored, not a second place to restructure the tree. */}
      {isBuilding && !optionsOnly && (
        <ConditionElements
          spreadIndex={spreadIndex}
          conditionGroup={conditionGroup}
          parentCondition={condition}
          elements={children}
          isEditing={conditionEditorOverlay.visible}
          textOnly={textOnly}
          className={styles.conditionElement}
        />
      )}
      {!isBuilding && (!optionsOnly || visibleChildren.length > 0) && (
        <PersonalizationElements
          childrenKey={getConditionChildrenKey(condition.id, condition.activeOptionId)}
          spreadIndex={spreadIndex}
          conditionGroup={conditionGroup}
          elements={visibleChildren}
          personalizationElementClassName={styles.conditionElement}
          optionsOnly={optionsOnly}
        />
      )}
    </div>
  );
}

export default React.memo(PersonalizationCondition);
